"use client";
import { createContext, useContext, useEffect, useState } from "react";
import CookieConsentDialog from "./cookie-consent-dialog";

interface CookieConsentContextType {
    showCookieDialog: () => void;
    hideCookieDialog: () => void;
    hasAcceptedCookies: boolean;
    hasDeclinedCookies: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export function useCookieConsent() {
    const context = useContext(CookieConsentContext);
    if (!context) {
        throw new Error("useCookieConsent must be used within a CookieConsentProvider");
    }
    return context;
}

interface CookieConsentProviderProps {
    children: React.ReactNode;
}

export default function CookieConsentProvider({ children }: CookieConsentProviderProps) {
    const [showDialog, setShowDialog] = useState(false);
    const [hasAccepted, setHasAccepted] = useState(false);
    const [hasDeclined, setHasDeclined] = useState(false);

    useEffect(() => {
        // Check if user has already made a choice
        const cookieChoice = localStorage.getItem("cookie-consent");

        if (!cookieChoice) {
            // Show dialog after a short delay to let the page load
            const timer = setTimeout(() => {
                setShowDialog(true);
            }, 2000); // 2 second delay

            return () => clearTimeout(timer);
        } else if (cookieChoice === "accepted") {
            setHasAccepted(true);
        } else if (cookieChoice === "declined") {
            setHasDeclined(true);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem("cookie-consent", "accepted");
        setHasAccepted(true);
        setShowDialog(false);
    };

    const handleDecline = () => {
        localStorage.setItem("cookie-consent", "declined");
        setHasDeclined(true);
        setShowDialog(false);
    };

    const handleClose = () => {
        setShowDialog(false);
    };

    const showCookieDialog = () => {
        setShowDialog(true);
    };

    const hideCookieDialog = () => {
        setShowDialog(false);
    };

    const value = {
        showCookieDialog,
        hideCookieDialog,
        hasAcceptedCookies: hasAccepted,
        hasDeclinedCookies: hasDeclined,
    };

    return (
        <CookieConsentContext.Provider value={value}>
            {children}
            <CookieConsentDialog
                isOpen={showDialog}
                onAccept={handleAccept}
                onReject={handleDecline}
                onClose={handleClose}
            />
        </CookieConsentContext.Provider>
    );
} 